import { useCallback, useEffect, useMemo, useState } from 'react';
import { computeCompletion, computeVisibility } from '@huaca/claro-engine';
import { useAutosave } from './autosave';
import { CompleteBar } from './CompleteBar';
import { uploadRoomFile } from './fileUpload';
import { ProgressMap } from './ProgressMap';
import { RoomApiError, roomFetch } from './roomFetch';
import { RoomSkeleton } from './RoomSkeleton';
import { SaveIndicator } from './SaveIndicator';
import { SectionView } from './SectionView';
import type { RoomAnswer, RoomState } from './types';

/**
 * The wizard — mounted once the email gate is passed.
 *
 * It loads `/room/state`, holds a working copy of the answers, and renders the form the client fills.
 * Visibility and completion are recomputed LOCALLY on every change via the shared engine, so the
 * form reacts instantly (a select revealing a follow-up field, a section flipping to "done"). The
 * server remains the authority — this phase does not yet persist (autosave is 5.5) or submit
 * (CompleteBar is 5.6); it establishes the render + navigation.
 */
export function RoomWizard() {
  const [state, setState] = useState<RoomState | null>(null);
  const [answers, setAnswers] = useState<Record<string, RoomAnswer>>({});
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [justCompleted, setJustCompleted] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { state: loaded } = await roomFetch<{ state: RoomState }>('room/state', { authenticated: true });
      setState(loaded);
      setAnswers(loaded.answers);
    } catch (err) {
      setError(err instanceof RoomApiError ? err.message : 'לא הצלחנו לטעון את הטופס. נסו לרענן.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Engine over the LOCAL answers — the source for what renders and what the progress map shows.
  const { visibleFieldIds, visibleSectionIds } = useMemo(
    () => (state ? computeVisibility(state.form, answers) : { visibleFieldIds: new Set<string>(), visibleSectionIds: new Set<string>() }),
    [state, answers],
  );
  const completion = useMemo(
    () => (state ? computeCompletion(state.form, answers) : { completedSectionIds: [], requiredOutstanding: 0 }),
    [state, answers],
  );

  // The current section defaults to the first VISIBLE one, and never points at a section a condition
  // has just hidden (else the client would be stranded on a blank pane).
  const visibleSections = useMemo(
    () => (state ? state.form.sections.filter((s) => visibleSectionIds.has(s.id)) : []),
    [state, visibleSectionIds],
  );
  const activeSection =
    visibleSections.find((s) => s.id === currentSectionId) ?? visibleSections[0] ?? null;

  const autosave = useAutosave();
  const setValue = useCallback(
    (fieldId: string, value: string) => {
      setAnswers((prev) => ({ ...prev, [fieldId]: { value, file: prev[fieldId]?.file ?? null } }));
      autosave.stage(fieldId, value);
    },
    [autosave],
  );

  // Files go through their OWN 3-step flow (upload-url → PUT → confirm), NOT the autosave path — the
  // server records the answer on confirm. We only mirror the result into local state so the engine
  // (visibility + completion) reacts, exactly as a scalar save would.
  const handleUpload = useCallback(async (fieldId: string, file: File, onProgress: (pct: number) => void) => {
    const uploaded = await uploadRoomFile(fieldId, file, (p) => onProgress(p.total ? Math.round((p.loaded / p.total) * 100) : 0));
    setAnswers((prev) => ({ ...prev, [fieldId]: { value: null, file: uploaded } }));
  }, []);

  const handleComplete = useCallback(async () => {
    // Persist any un-saved edits FIRST and await them — the server validates completion against the
    // persisted answers, so submitting before the last blur has landed would wrongly 409.
    await autosave.saveNow();
    try {
      await roomFetch('room/complete', { authenticated: true });
      setJustCompleted(true);
    } catch (err) {
      if (err instanceof RoomApiError && err.status === 409) {
        // The server sees a required field the client thought was filled (a save didn't land).
        // Reload the truth so the form + progress reflect reality, then tell the client plainly.
        await load();
        throw new RoomApiError(409, 'INCOMPLETE', 'חלק מהשדות עדיין חסרים. בדקו את הטופס ונסו שוב.');
      }
      throw err;
    }
  }, [autosave, load]);

  // Completed either just now, or because the room was re-opened after an earlier submission.
  const isCompleted = justCompleted || state?.status === 'completed';

  if (error) {
    return (
      <section className="room-card">
        <h1 className="room-card-title">משהו השתבש</h1>
        <p className="room-card-body" role="alert">
          {error}
        </p>
        <button type="button" className="btn-primary" onClick={() => void load()}>
          נסו שוב
        </button>
      </section>
    );
  }

  if (state && isCompleted) {
    return (
      <section className="room-card room-completed">
        <div className="room-completed-mark" aria-hidden="true">
          ✓
        </div>
        <h1 className="room-card-title">תודה! הפרטים נשלחו</h1>
        <p className="room-card-body">קיבלנו את המידע שמסרתם. אין צורך בפעולה נוספת — ניתן לסגור את החלון.</p>
      </section>
    );
  }

  if (!state || !activeSection) {
    return <RoomSkeleton label="טוען את הטופס…" />;
  }

  // Position within the VISIBLE sections drives the linear Continue/Back path. The stepper still
  // allows free jumps (setCurrentSectionId); these buttons are the forward/back convenience on top of
  // it. The last visible section's primary is the submit, not a "next" — there is nowhere to go.
  const activeIndex = visibleSections.findIndex((s) => s.id === activeSection.id);
  const isLastSection = activeIndex === visibleSections.length - 1;
  const goToOffset = (delta: number) => {
    const next = visibleSections[activeIndex + delta];
    if (next) setCurrentSectionId(next.id);
  };

  return (
    <div className="room-wizard">
      <div className="room-wizard-top">
        <ProgressMap
          sections={state.form.sections}
          visibleSectionIds={visibleSectionIds}
          completedSectionIds={completion.completedSectionIds}
          currentSectionId={activeSection.id}
          onNavigate={setCurrentSectionId}
        />
        <SaveIndicator status={autosave.status} />
      </div>
      <SectionView
        section={activeSection}
        visibleFieldIds={visibleFieldIds}
        answers={answers}
        onChange={setValue}
        onBlur={() => autosave.flush()}
        onUpload={handleUpload}
      />
      <div className="room-footer">
        {activeIndex > 0 && (
          <button type="button" className="btn-ghost room-back" onClick={() => goToOffset(-1)}>
            <span aria-hidden="true">→ </span>חזרה
          </button>
        )}
        <div className="room-footer-primary">
          {isLastSection ? (
            <CompleteBar outstanding={completion.requiredOutstanding} onComplete={handleComplete} />
          ) : (
            <button type="button" className="btn-primary room-next" onClick={() => goToOffset(1)}>
              המשך<span aria-hidden="true"> ←</span>
            </button>
          )}
        </div>
      </div>
      <p className="room-stepcount">
        שלב {activeIndex + 1} מתוך {visibleSections.length}
      </p>
    </div>
  );
}
